import { BALANCE } from './balance';
import { ITEMS, STORES, item } from './items';
import { carriedValue, dropAll, tickEffects } from './inventory';
import { Rng, mintSeed, seedName } from './rng';
import type {
  ContainerId,
  ContainerState,
  ItemId,
  PlayerId,
  PlayerState,
  RoundConfig,
  RoundPhase,
  StoreId,
  Team,
  Vec3,
} from './types';

/**
 * THE ROUND.
 *
 * Host-authoritative and completely pure: no rendering, no physics, no sockets.
 * The host feeds it player positions and intents; it produces state and events.
 * A headless bot client runs the exact same object, which is how the game gets
 * tested without a GPU or a human.
 */

export type DropoffKind = 'solo' | 'dock' | 'extraction';

export interface DropoffDef {
  id: string;
  kind: DropoffKind;
  pos: Vec3;
  /** How close you must be to use it. */
  radius: number;
}

export type RoundEvent =
  | { t: 'phase'; phase: RoundPhase }
  | { t: 'breach'; by: PlayerId; container: ContainerId; noise: number }
  | { t: 'loot'; by: PlayerId; item: ItemId; container: ContainerId }
  | { t: 'deposit'; kind: DropoffKind; by: PlayerId; amount: number; items: number; assists: PlayerId[] }
  | { t: 'capture'; guard: PlayerId; thief: PlayerId; at: Vec3; dropped: LooseDrop[] }
  | { t: 'guardDown'; guard: PlayerId; by: PlayerId }
  | { t: 'noise'; by: PlayerId; at: Vec3; radius: number }
  | { t: 'end'; reason: 'timer' | 'all-thieves-out' };

/** Something that hit the floor, named so the renderer can put a mesh on it. */
export interface LooseDrop {
  id: string;
  item: ItemId;
  pos: Vec3;
}

export interface Refusal {
  ok: false;
  reason: string;
}
export interface DepositOk {
  ok: true;
  amount: number;
  items: number;
  assists: PlayerId[];
}
export type DepositResult = DepositOk | Refusal;

export interface ScoreLine {
  id: PlayerId;
  name: string;
  team: Team;
  banked: number;
  /** Value still on their person when the round ended. Worth nothing. */
  unbanked: number;
  status: string;
}

/** Tonight's delivery: what turned up, where, and what the sign says about it. */
export interface Consignment {
  store: StoreId;
  storeName: string;
  item: ItemId;
  count: number;
  /** Full-rate dollars if you get all of it out. */
  value: number;
  /** The mall's own words for it, shown on the briefing card. */
  label: string;
}

/**
 * The consignment table.
 *
 * Every entry is worth roughly what the jewellery shop is worth, and they differ
 * in the only dimension that matters once the money is equal: what it costs to
 * carry. Diamonds are twenty grams and a free night. Five televisions are ninety
 * kilos, which is two trips minimum with a bag that can hold forty-five, and the
 * second trip is through a mall that now knows you are in it. The same headline
 * number produces a completely different night.
 */
const CONSIGNMENTS: ReadonlyArray<{ item: ItemId; count: number; label: string }> = [
  { item: 'safeCash', count: 2, label: 'NIGHT DEPOSIT NOT COLLECTED' },
  { item: 'diamonds', count: 1, label: 'UNINSURED PARCEL IN TRANSIT' },
  { item: 'opioids', count: 4, label: 'SCHEDULE II TRANSFER PENDING' },
  { item: 'television', count: 5, label: 'PALLET AWAITING FLOOR SPACE' },
  { item: 'firstEdition', count: 4, label: 'ESTATE LOT, UNCATALOGUED' },
  { item: 'watch', count: 3, label: 'SERVICE RETURNS, UNCLAIMED' },
  { item: 'rifle', count: 6, label: 'TRANSFER HELD PENDING PAPERWORK' },
  { item: 'laptop', count: 7, label: 'STAFF ROLLOUT, BOXED' },
];

const REFUSE = (reason: string): Refusal => ({ ok: false, reason });

export class Round {
  readonly config: RoundConfig;
  phase: RoundPhase = 'lobby';
  /** Seconds left in the current phase. */
  clock: number;
  readonly players = new Map<PlayerId, PlayerState>();
  readonly containers = new Map<ContainerId, ContainerState>();
  readonly dropoffs: DropoffDef[] = [];
  /** Loot lying on the floor: dropped by a caught thief, or never picked up. */
  loose: Array<{ id: string; item: ItemId; pos: Vec3; store: StoreId | null }> = [];
  /** Drained by the host each tick and forwarded to clients. */
  events: RoundEvent[] = [];
  /** Set by `stockContainers`. Null until the building has been stocked. */
  consignment: Consignment | null = null;

  private looseSeq = 0;
  private endReason: 'timer' | 'all-thieves-out' | null = null;

  constructor(config: Partial<RoundConfig> = {}) {
    this.config = {
      durationSec: config.durationSec ?? BALANCE.round.durationSec,
      briefingSec: config.briefingSec ?? BALANCE.round.briefingSec,
      seed: config.seed ?? mintSeed(),
    };
    this.clock = this.config.briefingSec;
  }

  /** The night's four-letter name, for the briefing card and bug reports. */
  get name(): string {
    return seedName(this.config.seed);
  }

  /**
   * A generator for one named subsystem of this round.
   *
   * Per-subsystem rather than one shared stream, because a shared stream couples
   * things that have no business being coupled: add one call to the container
   * dealer and every guard in the building starts their beat somewhere else. Each
   * caller gets its own sequence off the same seed, so a change to how loot is
   * dealt cannot silently reshuffle the patrols.
   */
  rngFor(subsystem: string): Rng {
    let h = this.config.seed >>> 0;
    for (let i = 0; i < subsystem.length; i++) {
      h = (Math.imul(h ^ subsystem.charCodeAt(i), 0x01000193) + 1) >>> 0;
    }
    return new Rng(h);
  }

  // --- Setup -------------------------------------------------------------

  addPlayer(id: PlayerId, name: string, team: Team, pos: Vec3 = origin()): PlayerState {
    const p: PlayerState = {
      id,
      name,
      team,
      status: 'active',
      pos: { ...pos },
      carried: [],
      tools: [],
      banked: 0,
      effects: [],
    };
    this.players.set(id, p);
    return p;
  }

  addDropoff(d: DropoffDef): void {
    this.dropoffs.push(d);
  }

  /**
   * Puts an item on the floor and returns its id. The one place loose ids are
   * minted, so the world's meshes and the sim's list can never disagree about
   * what is lying where.
   */
  addLoose(itemId: ItemId, pos: Vec3, store: StoreId | null = null): string {
    const id = `loose${this.looseSeq++}`;
    this.loose.push({ id, item: itemId, pos: { ...pos }, store });
    return id;
  }

  /**
   * The same thing, with the id already decided.
   *
   * A client does not mint loose ids — it is told them, because a mesh on one
   * machine and a record on another have to answer to the same name for "pick
   * that up" to mean anything. The counter is nudged past whatever it is given
   * so a client promoted to host later cannot re-issue an id already in play.
   */
  addLooseWithId(id: string, itemId: ItemId, pos: Vec3, store: StoreId | null = null): void {
    if (this.loose.some((l) => l.id === id)) return;
    this.loose.push({ id, item: itemId, pos: { ...pos }, store });
    const n = Number.parseInt(id.replace('loose', ''), 10);
    if (Number.isFinite(n) && n >= this.looseSeq) this.looseSeq = n + 1;
  }

  /**
   * Voluntarily dumps everything carried, tools included, at the player's feet.
   *
   * The escape hatch for the weight system: 28kg of medieval armour is a fine
   * decision right up until you hear a guard, and a game that makes you keep it
   * is a game where you stop picking it up. Returns what hit the floor so the
   * caller can spawn the meshes.
   */
  dropCarried(by: PlayerId): Array<{ id: string; item: ItemId; pos: Vec3 }> {
    const p = this.players.get(by);
    if (!p) return [];
    const out: Array<{ id: string; item: ItemId; pos: Vec3 }> = [];
    // Fan them out slightly, or eight items land inside each other and the
    // targeting can only ever see one of them.
    const spill = dropAll(p);
    for (let i = 0; i < spill.length; i++) {
      const a = (i / Math.max(1, spill.length)) * Math.PI * 2;
      const r = spill.length > 1 ? 0.45 : 0;
      const pos = { x: p.pos.x + Math.cos(a) * r, y: p.pos.y, z: p.pos.z + Math.sin(a) * r };
      out.push({ id: this.addLoose(spill[i].item, pos, spill[i].store), item: spill[i].item, pos });
    }
    return out;
  }

  /**
   * Populates containers from the store tables. Containers are supplied by the
   * level (a vitrine knows where it is); the sim only decides what goes inside.
   *
   * Two rules survive from the deterministic version because they are what makes
   * the building readable: the best item in a store is always behind the hardest
   * lock in that store, and every container gets a share, so no store is one good
   * case and three empty shelves. Everything the two rules do not pin down is
   * dealt from the night's seed — which shelf, which case, in what order — so a
   * player who has learned the mall still has to search it.
   */
  stockContainers(defs: Array<{ id: ContainerId; store: StoreId; lock?: ContainerState['lock'] }>): void {
    const rng = this.rngFor('loot');

    const byStore = new Map<StoreId, ItemId[]>();
    for (const store of Object.values(STORES)) {
      const pool: ItemId[] = [];
      for (const s of store.spawns) for (let i = 0; i < s.count; i++) pool.push(s.item);
      byStore.set(store.id, pool);
    }

    const cursor = new Map<StoreId, number>();
    const perStoreContainers = new Map<StoreId, ContainerId[]>();
    for (const d of defs) {
      if (!perStoreContainers.has(d.store)) perStoreContainers.set(d.store, []);
      perStoreContainers.get(d.store)!.push(d.id);
    }

    for (const d of defs) {
      this.containers.set(d.id, {
        id: d.id,
        store: d.store,
        lock: d.lock ?? 'none',
        contents: [],
        open: (d.lock ?? 'none') === 'none',
      });
    }

    this.consignment = this.chooseConsignment(perStoreContainers, rng);

    for (const [storeId, pool] of byStore) {
      const ids = perStoreContainers.get(storeId);
      if (!ids || ids.length === 0) continue;
      // Shuffle first, THEN rank. The sort is stable, so shuffling underneath it
      // leaves the lock ordering intact while scrambling the order of everything
      // that shares a rank — which is every shelf run in the mall.
      const ranked = rng
        .shuffle(ids)
        .sort((a, b) => lockRank(this.containers.get(b)!.lock) - lockRank(this.containers.get(a)!.lock));

      const extra = this.consignment?.store === storeId ? this.consignment.count : 0;
      for (let i = 0; i < extra; i++) pool.push(this.consignment!.item);

      const sorted = [...pool].sort((a, b) => ITEMS[b].value - ITEMS[a].value);
      for (const it of sorted) {
        const k = cursor.get(storeId) ?? 0;
        this.containers.get(ranked[k % ranked.length])!.contents.push(it);
        cursor.set(storeId, k + 1);
      }
    }
  }

  /**
   * TONIGHT'S CONSIGNMENT.
   *
   * One store gets a delivery it has no business holding, and it is worth about
   * as much as the jewellery shop. This is the thing that stops the fixed map
   * from having one correct route: the optimal night is jewellery-and-out until
   * the briefing says there is fourteen thousand dollars of banded cash sitting in
   * the skate shop, and then it is not.
   *
   * Jewellery is excluded as a destination. It is already the richest room in the
   * building and stacking the headline on top of it would produce the one outcome
   * this feature exists to prevent — a night that plays exactly like the last one.
   */
  private chooseConsignment(
    perStore: Map<StoreId, ContainerId[]>,
    rng: Rng,
  ): Consignment | null {
    const candidates = [...perStore.keys()].filter((s) => s !== 'jewellery').sort();
    const store = rng.pick(candidates);
    if (!store) return null;
    const pick = CONSIGNMENTS[rng.int(CONSIGNMENTS.length)];
    return {
      store,
      item: pick.item,
      count: pick.count,
      value: ITEMS[pick.item].value * pick.count,
      storeName: STORES[store]?.name ?? store.toUpperCase(),
      label: pick.label,
    };
  }

  // --- Lifecycle ---------------------------------------------------------

  start(): void {
    this.phase = 'briefing';
    this.clock = this.config.briefingSec;
    this.events.push({ t: 'phase', phase: 'briefing' });
  }

  tick(dt: number): void {
    if (this.phase === 'lobby' || this.phase === 'results') return;

    for (const p of this.players.values()) tickEffects(p, dt);

    this.clock -= dt;
    if (this.phase === 'briefing' && this.clock <= 0) {
      this.phase = 'active';
      this.clock = this.config.durationSec;
      this.events.push({ t: 'phase', phase: 'active' });
      return;
    }

    if (this.phase !== 'active') return;

    if (this.clock <= 0) {
      this.clock = 0;
      this.finish('timer');
      return;
    }
    if (this.thievesRemaining() === 0 && this.countThieves() > 0) {
      this.finish('all-thieves-out');
    }
  }

  private finish(reason: 'timer' | 'all-thieves-out'): void {
    this.phase = 'results';
    this.endReason = reason;
    this.events.push({ t: 'end', reason });
    this.events.push({ t: 'phase', phase: 'results' });
  }

  get reason(): 'timer' | 'all-thieves-out' | null {
    return this.endReason;
  }

  countThieves(): number {
    let n = 0;
    for (const p of this.players.values()) if (p.team === 'thief') n++;
    return n;
  }

  /** Thieves still in the mall and still playable. */
  thievesRemaining(): number {
    let n = 0;
    for (const p of this.players.values()) {
      if (p.team === 'thief' && p.status === 'active') n++;
    }
    return n;
  }

  // --- Actions -----------------------------------------------------------

  /**
   * Opens a container. The caller has already run the breach timer and verified
   * the tool; this is the commit step, so it is the one that makes noise.
   */
  breach(by: PlayerId, containerId: ContainerId, withTool: ItemId | null): Refusal | { ok: true } {
    const p = this.requireActiveThief(by);
    if ('reason' in p) return p;
    const c = this.containers.get(containerId);
    if (!c) return REFUSE('no such container');
    if (c.open) return REFUSE('already open');

    if (c.lock !== 'none') {
      if (!withTool) return REFUSE(`needs a tool for ${c.lock}`);
      const tool = ITEMS[withTool];
      if (!tool || tool.kind !== 'tool') return REFUSE('not a tool');
      if (tool.defeats !== c.lock) return REFUSE(`${tool.name} does not defeat ${c.lock}`);
      if (!p.tools.includes(withTool)) return REFUSE('tool not carried');
    }

    c.open = true;
    const noise = withTool ? (ITEMS[withTool].noise ?? 0) : 0;
    this.events.push({ t: 'breach', by, container: containerId, noise });
    if (noise > 0) this.events.push({ t: 'noise', by, at: { ...p.pos }, radius: noise });
    return { ok: true };
  }

  /** Takes one named item out of an open container into the thief's hands. */
  takeFrom(by: PlayerId, containerId: ContainerId, itemId: ItemId): Refusal | { ok: true } {
    const p = this.requireActiveThief(by);
    if ('reason' in p) return p;
    const c = this.containers.get(containerId);
    if (!c) return REFUSE('no such container');
    if (!c.open) return REFUSE('locked');
    const idx = c.contents.indexOf(itemId);
    if (idx < 0) return REFUSE('not in this container');

    // Import cycle avoidance: canPickUp lives in inventory and only reads state.
    const check = pickUpChecked(p, itemId, c.store);
    if (!check.ok) return REFUSE(check.reason ?? 'cannot carry');

    c.contents.splice(idx, 1);
    this.events.push({ t: 'loot', by, item: itemId, container: containerId });
    return { ok: true };
  }

  /**
   * Deposit everything the thief is carrying at a drop-off.
   *
   * Solo chute: 65%, always available, credited entirely to the depositor.
   * Loading dock: 100% plus a 15% team bonus, but it needs a second thief
   * standing on it — and every assisting thief earns 10% of the haul on top,
   * created out of nothing. Coordinating is strictly the best play, and it is
   * also the loudest, which is the entire tension of the mode.
   */
  deposit(by: PlayerId, dropoffId: string): DepositResult {
    const p = this.requireActiveThief(by);
    if ('reason' in p) return p;
    const d = this.dropoffs.find((x) => x.id === dropoffId);
    if (!d) return REFUSE('no such drop-off');
    if (dist(p.pos, d.pos) > d.radius) return REFUSE('too far from the drop-off');

    const value = carriedValue(p);
    const count = p.carried.filter((s) => ITEMS[s.item]?.kind === 'loot').length;
    if (count === 0) return REFUSE('nothing to deposit');

    const assists: PlayerId[] = [];
    let rate: number;

    if (d.kind === 'dock') {
      const cfg = BALANCE.dropoff.dock;
      for (const other of this.players.values()) {
        if (other.id === by || other.team !== 'thief' || other.status !== 'active') continue;
        if (dist(other.pos, d.pos) <= cfg.assistRadius) assists.push(other.id);
      }
      if (assists.length + 1 < cfg.minThieves) {
        return REFUSE(`the dock needs ${cfg.minThieves} thieves`);
      }
      rate = cfg.payoutRate * (1 + cfg.teamBonus);
    } else if (d.kind === 'solo') {
      rate = BALANCE.dropoff.solo.payoutRate;
    } else {
      rate = BALANCE.dropoff.extraction.payoutRate;
    }

    const amount = Math.round(value * rate);
    p.banked += amount;
    p.carried = p.carried.filter((s) => ITEMS[s.item]?.kind !== 'loot');

    for (const a of assists) {
      const helper = this.players.get(a)!;
      helper.banked += Math.round(value * BALANCE.dropoff.dock.assistShare);
    }

    const noise =
      d.kind === 'dock' ? BALANCE.dropoff.dock.noise : d.kind === 'solo' ? BALANCE.dropoff.solo.noise : 0;
    if (noise > 0) this.events.push({ t: 'noise', by, at: { ...d.pos }, radius: noise });
    this.events.push({ t: 'deposit', kind: d.kind, by, amount, items: count, assists });
    return { ok: true, amount, items: count, assists };
  }

  /** Walk out of the mall. Banks what you carry at a penalty and ends your round. */
  extract(by: PlayerId, dropoffId: string): DepositResult {
    const p = this.requireActiveThief(by);
    if ('reason' in p) return p;
    const d = this.dropoffs.find((x) => x.id === dropoffId && x.kind === 'extraction');
    if (!d) return REFUSE('no such exit');
    if (dist(p.pos, d.pos) > d.radius) return REFUSE('too far from the exit');

    const value = carriedValue(p);
    const count = p.carried.filter((s) => ITEMS[s.item]?.kind === 'loot').length;
    const amount = Math.round(value * BALANCE.dropoff.extraction.payoutRate);
    p.banked += amount;
    p.carried = [];
    p.status = 'extracted';
    this.events.push({ t: 'deposit', kind: 'extraction', by, amount, items: count, assists: [] });
    this.checkAllOut();
    return { ok: true, amount, items: count, assists: [] };
  }

  /**
   * A guard puts hands on a thief. Permanent for the round — that is the whole
   * reason a thief thinks twice about one more store. Everything they were
   * carrying hits the floor where they stood, and stays lootable.
   */
  capture(guardId: PlayerId, thiefId: PlayerId): Refusal | { ok: true; spilled: number; dropped: LooseDrop[] } {
    const g = this.players.get(guardId);
    if (!g || g.team !== 'guard') return REFUSE('not a guard');
    if (g.status !== 'active') return REFUSE('guard is down');
    const t = this.requireActiveThief(thiefId);
    if ('reason' in t) return t;
    if (dist(g.pos, t.pos) > BALANCE.capture.reachM) return REFUSE('out of reach');

    const spill = BALANCE.capture.dropsLootOnCapture ? dropAll(t) : [];
    // Fanned out for the same reason a voluntary drop is: eight items at one
    // point are one item as far as the targeting is concerned.
    const dropped: LooseDrop[] = [];
    for (let i = 0; i < spill.length; i++) {
      const a = (i / Math.max(1, spill.length)) * Math.PI * 2;
      const pos = { x: t.pos.x + Math.cos(a) * 0.45, y: t.pos.y, z: t.pos.z + Math.sin(a) * 0.45 };
      const id = `loose${this.looseSeq++}`;
      this.loose.push({ id, item: spill[i].item, pos, store: spill[i].store });
      dropped.push({ id, item: spill[i].item, pos });
    }
    t.status = 'caught';
    // The list, not the count. The caller has to build a mesh for each one or a
    // caught thief's bag becomes loot that exists only in the simulation.
    this.events.push({ t: 'capture', guard: guardId, thief: thiefId, at: { ...t.pos }, dropped });
    this.checkAllOut();
    return { ok: true, spilled: spill.length, dropped };
  }

  /** A thief shoves a guard over. Guards get back up; that asymmetry is the point. */
  downGuard(guardId: PlayerId, by: PlayerId): Refusal | { ok: true } {
    const g = this.players.get(guardId);
    if (!g || g.team !== 'guard') return REFUSE('not a guard');
    if (g.status === 'downed') return REFUSE('already down');
    g.status = 'downed';
    this.events.push({ t: 'guardDown', guard: guardId, by });
    return { ok: true };
  }

  reviveGuard(guardId: PlayerId): void {
    const g = this.players.get(guardId);
    if (g && g.team === 'guard') g.status = 'active';
  }

  /**
   * Picks up something lying on the floor.
   *
   * The one thief action allowed during the briefing, because the briefing is
   * when you kit up: the tools are laid out on a table on the forecourt and
   * choosing two of them is the decision the round is built around. Refusing it
   * until the clock starts would mean every round opens with fifteen seconds of
   * standing next to a crowbar you are not allowed to touch.
   */
  takeLoose(by: PlayerId, looseId: string): Refusal | { ok: true } {
    const p = this.requireActiveThief(by, ['briefing', 'active']);
    if ('reason' in p) return p;
    const idx = this.loose.findIndex((l) => l.id === looseId);
    if (idx < 0) return REFUSE('nothing there');
    const l = this.loose[idx];
    const check = pickUpChecked(p, l.item, l.store);
    if (!check.ok) return REFUSE(check.reason ?? 'cannot carry');
    this.loose.splice(idx, 1);
    return { ok: true };
  }

  // --- Reporting ---------------------------------------------------------

  scoreboard(): ScoreLine[] {
    const lines: ScoreLine[] = [];
    for (const p of this.players.values()) {
      lines.push({
        id: p.id,
        name: p.name,
        team: p.team,
        banked: p.banked,
        unbanked: carriedValue(p),
        status: p.status,
      });
    }
    return lines.sort((a, b) => b.banked - a.banked);
  }

  /**
   * Everything still in the building: unlooted containers plus what is lying on
   * the floor.
   *
   * This is the number on the results card that does the work. A total banked is
   * a fact about the night that just ended; eighty-one thousand dollars still
   * sitting in a mall you had eight minutes in is an argument about the next one.
   */
  floorValue(): number {
    let left = 0;
    for (const c of this.containers.values()) {
      for (const it of c.contents) left += ITEMS[it]?.value ?? 0;
    }
    for (const l of this.loose) left += ITEMS[l.item]?.value ?? 0;
    // What a caught thief was carrying is already back on the floor as loose
    // drops; what an extracted one carried is gone. Neither is counted twice.
    return left;
  }

  /** What the guards scored: everything the thieves failed to bank. */
  guardScore(): number {
    let denied = 0;
    for (const p of this.players.values()) {
      if (p.team !== 'thief') continue;
      if (p.status === 'caught') denied += 1;
    }
    return denied;
  }

  drainEvents(): RoundEvent[] {
    const e = this.events;
    this.events = [];
    return e;
  }

  private checkAllOut(): void {
    if (this.phase === 'active' && this.countThieves() > 0 && this.thievesRemaining() === 0) {
      this.finish('all-thieves-out');
    }
  }

  private requireActiveThief(id: PlayerId, phases: RoundPhase[] = ['active']): PlayerState | Refusal {
    const p = this.players.get(id);
    if (!p) return REFUSE('no such player');
    if (p.team !== 'thief') return REFUSE('not a thief');
    if (p.status !== 'active') return REFUSE(`thief is ${p.status}`);
    if (!phases.includes(this.phase)) return REFUSE(`round is ${this.phase}`);
    return p;
  }
}

// --- helpers -------------------------------------------------------------

function pickUpChecked(p: PlayerState, id: ItemId, store: StoreId | null) {
  // Local re-implementation of the pickup gate to keep round.ts free of a
  // circular import back through inventory -> items -> round.
  const def = item(id);
  if (def.kind === 'tool') {
    if (p.tools.length >= BALANCE.carry.toolSlots) return { ok: false, reason: 'tool slots full' };
  } else if (p.carried.length >= BALANCE.carry.itemSlots) {
    return { ok: false, reason: 'hands full' };
  }
  let kg = 0;
  for (const s of p.carried) kg += ITEMS[s.item].weight;
  for (const t of p.tools) kg += ITEMS[t].weight;
  if (kg + def.weight > BALANCE.carry.capacityKg) return { ok: false, reason: 'too heavy' };
  if (def.kind === 'tool') p.tools.push(id);
  else p.carried.push({ item: id, store });
  return { ok: true as const };
}

function lockRank(l: ContainerState['lock']): number {
  return ['none', 'force', 'pick', 'card', 'cut', 'safe'].indexOf(l);
}

function dist(a: Vec3, b: Vec3): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  const dz = a.z - b.z;
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

const origin = (): Vec3 => ({ x: 0, y: 0, z: 0 });
